import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ButtonBase from "@mui/material/ButtonBase";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";

import { CARD_SX } from "/imports/ui/components/booking";
import { formatPrice } from "../classHelpers";

// Desktop week grid — one column per day, class chips stacked per day. Each chip
// opens the shared options popover anchored to it.
function ClassWeekGrid({ cls }) {
  const { weekDates, classesByDate, currency, todayISO, handleClassChipClick } = cls;

  const renderWeekClassChip = (c) => {
    const isFull = c.spotsAvailable === 0;
    const joined = c.isJoined;
    return (
      <ButtonBase
        key={c._id}
        onClick={(e) => handleClassChipClick(e, c)}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          px: 1,
          py: 0.7,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: joined ? "success.main" : "divider",
          bgcolor: joined ? "rgba(76,175,80,0.06)" : "#fff",
          opacity: isFull && !joined ? 0.6 : 1,
          transition: "all 0.12s ease",
          "&:hover": { borderColor: "primary.main", bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }
        }}
      >
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, lineHeight: 1.15, color: "text.primary" }}>{c.time}</Typography>
        <Typography
          sx={{
            fontSize: "0.66rem",
            fontWeight: 600,
            lineHeight: 1.25,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%"
          }}
        >
          {c.name}
        </Typography>
        <Typography
          sx={{ fontSize: "0.6rem", fontWeight: 600, lineHeight: 1.3, color: joined ? "success.main" : isFull ? "error.main" : "primary.main" }}
        >
          {joined ? "Joined" : isFull ? "Full" : formatPrice(c.price, currency)}
        </Typography>
      </ButtonBase>
    );
  };

  return (
    <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
        {weekDates.map((d, i) => {
          const dt = DateTime.fromISO(d);
          const isToday = d === todayISO;
          const isPast = d < todayISO;
          const dayClasses = classesByDate.get(d) || [];
          return (
            <Box key={d} sx={{ borderLeft: i > 0 ? "1px solid" : "none", borderColor: "divider", opacity: isPast ? 0.5 : 1 }}>
              <Box
                sx={{
                  textAlign: "center",
                  py: 1.25,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: isToday ? (theme) => alpha(theme.palette.primary.main, 0.1) : "transparent"
                }}
              >
                <Typography
                  sx={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }}
                >
                  {dt.toFormat("ccc")}
                </Typography>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, lineHeight: 1.2, color: isToday ? "primary.main" : "text.primary" }}>
                  {dt.day}
                </Typography>
                {/* A week can straddle two months, and a column of bare numbers says
                    nothing about which one this is. */}
                <Typography
                  sx={{
                    fontSize: "0.55rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    lineHeight: 1,
                    color: isToday ? "primary.main" : "text.secondary",
                    opacity: 0.85
                  }}>
                  {dt.toFormat("LLL")}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 0.75,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  minHeight: "5.625rem",
                  bgcolor: isToday ? (theme) => alpha(theme.palette.primary.main, 0.03) : "transparent"
                }}
              >
                {dayClasses.map((c) => renderWeekClassChip(c))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

export default ClassWeekGrid;
